import { h } from 'vue';
import type { Slots, VNodeChild } from 'vue';

import {
  CUSTOM_HEADER_SLOT_ID,
  HEADER_METADATA_SLOT_ID,
  HEADER_PREFIX_SLOT_ID,
} from '../../constants';
import type { GetHoveredLineResult } from '../../managers/InteractionManager';
import type { FileContents, LineAnnotation } from '../../types';
import { getLineAnnotationName } from '../../utils/getLineAnnotationName';
import { GutterUtilitySlotStyles } from '../constants';

// The component passes its raw `ctx.slots`; the public scoped-slot contract is
// documented by `FileSlotsVue` in ../types.
interface RenderFileChildrenProps {
  file: FileContents;
  slots: Slots;
  lineAnnotations: LineAnnotation[] | undefined;
  getHoveredLine(): GetHoveredLineResult<'file'> | undefined;
}

// Build the light-DOM children for a `<diffs-container>` rendering a single
// file. Each provided scoped slot becomes a `<div slot="…">` whose name matches
// the shadow-DOM `<slot>` the imperative core created, so the browser projects
// the user's Vue content into the correct place.
export function renderFileChildren({
  file,
  slots,
  lineAnnotations,
  getHoveredLine,
}: RenderFileChildrenProps): VNodeChild[] {
  const children: VNodeChild[] = [];

  if (slots.header != null) {
    children.push(
      h('div', { slot: CUSTOM_HEADER_SLOT_ID }, slots.header({ file }))
    );
  } else {
    if (slots.headerPrefix != null) {
      children.push(
        h('div', { slot: HEADER_PREFIX_SLOT_ID }, slots.headerPrefix({ file }))
      );
    }
    if (slots.headerMetadata != null) {
      children.push(
        h(
          'div',
          { slot: HEADER_METADATA_SLOT_ID },
          slots.headerMetadata({ file })
        )
      );
    }
  }

  if (slots.annotation != null && lineAnnotations != null) {
    const renderAnnotation = slots.annotation;
    lineAnnotations.forEach((annotation, index) => {
      children.push(
        h(
          'div',
          { key: index, slot: getLineAnnotationName(annotation) },
          renderAnnotation({ annotation })
        )
      );
    });
  }

  if (slots.gutter != null) {
    children.push(
      h(
        'div',
        { slot: 'gutter-utility-slot', style: GutterUtilitySlotStyles },
        slots.gutter({ getHoveredLine })
      )
    );
  }

  return children;
}
